/**************************************************/
/* Methods for PCA and PCVPCA based on wasm code  */
/**************************************************/

import { ismatrix, Matrix } from 'mdatools/arrays';
import { isArrCorrect, checkedIndexCopy, alloc, allocCopy, checkModelBuffer, wasmError }
   from './utils.js';


/**
 * Returns length of array with PCA results.
 *
 * @param {number} nrows number of rows in a dataset (without outliers).
 * @param {number} ncomp number of components in the model.
 * @param {number} ncols number of columns in a dataset (without excluded variables).
 *
 * @returns {number} the length of the results array.
 */
export function pca_getreslength(nrows, ncomp, ncols) {

   // Structure of PCA results:
   // - T: nr x ncomp
   // - H: nr x ncomp
   // - Q: nr x ncomp
   // - expvar: 1 x ncomp
   // - Fc: nr x ncomp
   // - Fr: nr x ncomp

   // - G: nc x ncomp
   // - R: nc x ncomp
   // - Ic: nc x ncomp
   // - Ir: nc x ncomp

   // - E: nc * nr
   return (5 * nrows * ncomp) + ncomp + (4 * ncols * ncomp) + nrows * ncols;
}


/**
 * Returns length of array with PCA model.
 *
 * @param {number} ncols number of columns in calibration set (without excluded).
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function pca_getmodellength(ncols, ncomp) {

   // Structure of PCA model:
   // - nc and ncomp: 2
   // - mX: 1 x ncols
   // - sX: 1 x ncols
   // - P: ncols x ncomp
   // - eigenvals: 1 x ncomp
   // - cq0: 1 x ncomp
   // - cNq: 1 x ncomp
   // - rq0: 1 x ncomp
   // - rNq: 1 x ncomp
   // - ch0: 1 x ncomp
   // - cNh: 1 x ncomp
   // - rh0: 1 x ncomp
   // - rNh: 1 x ncomp

   // - cr0: 1 x ncomp
   // - cNr: 1 x ncomp
   // - rr0: 1 x ncomp
   // - rNr: 1 x ncomp
   // - cg0: 1 x ncomp
   // - cNg: 1 x ncomp
   // - rg0: 1 x ncomp
   // - rNg: 1 x ncomp

   return 2 + 2 * ncols + ncols * ncomp + 17 * ncomp;
}


/**
 * Returns part of PCA model array for given outcome.
 *
 * @param {TypedArray} m array with PCA model.
 * @param {string} name name of the outcome (e.g. 'mX', 'sX', 'loadings', etc.).
 * @param {number} a component number (for loadings).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcamodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcamodel_get: parameter "m" must be JSON with PCA model.');
   }

   const ncols = m.ncols;
   const ncomp = m.ncomp;
   let offset, length;

   switch(name) {
      case 'mX':
         offset = 2;
         length = ncols;
         break;
      case 'sX':
         offset = 2 + ncols;
         length = ncols;
         break;
      case 'loadings':
      case 'xloadings':
         offset = 2 + 2 * ncols + (a - 1) * ncols;
         length = ncols;
         break;
      case 'P':
         offset = 2 + 2 * ncols;
         length = ncomp * ncols;
         break;
      case 'eigenvals':
      case 'xeigenvals':
         offset = 2 + 2 * ncols + ncols * ncomp;
         length = ncomp;
         break;
      case 'cq0':
         offset = 2 + 2 * ncols + ncols * ncomp + ncomp;
         length = ncomp;
         break;
      case 'cNq':
         offset = 2 + 2 * ncols + ncols * ncomp + 2 * ncomp;
         length = ncomp;
         break;
      case 'rq0':
         offset = 2 + 2 * ncols + ncols * ncomp + 3 * ncomp;
         length = ncomp;
         break;
      case 'rNq':
         offset = 2 + 2 * ncols + ncols * ncomp + 4 * ncomp;
         length = ncomp;
         break;
      case 'ch0':
         offset = 2 + 2 * ncols + ncols * ncomp + 5 * ncomp;
         length = ncomp;
         break;
      case 'cNh':
         offset = 2 + 2 * ncols + ncols * ncomp + 6 * ncomp;
         length = ncomp;
         break;
      case 'rh0':
         offset = 2 + 2 * ncols + ncols * ncomp + 7 * ncomp;
         length = ncomp;
         break;
      case 'rNh':
         offset = 2 + 2 * ncols + ncols * ncomp + 8 * ncomp;
         length = ncomp;
         break;
      case 'cr0':
         offset = 2 + 2 * ncols + ncols * ncomp + 9 * ncomp;
         length = ncomp;
         break;
      case 'cNr':
         offset = 2 + 2 * ncols + ncols * ncomp + 10 * ncomp;
         length = ncomp;
         break;
      case 'rr0':
         offset = 2 + 2 * ncols + ncols * ncomp + 11 * ncomp;
         length = ncomp;
         break;
      case 'rNr':
         offset = 2 + 2 * ncols + ncols * ncomp + 12 * ncomp;
         length = ncomp;
         break;
      case 'cg0':
         offset = 2 + 2 * ncols + ncols * ncomp + 13 * ncomp;
         length = ncomp;
         break;
      case 'cNg':
         offset = 2 + 2 * ncols + ncols * ncomp + 14 * ncomp;
         length = ncomp;
         break;
      case 'rg0':
         offset = 2 + 2 * ncols + ncols * ncomp + 15 * ncomp;
         length = ncomp;
         break;
      case 'rNg':
         offset = 2 + 2 * ncols + ncols * ncomp + 16 * ncomp;
         length = ncomp;
         break;

      default:
         throw Error('pcamodel_get: wrong outcome name.');
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns part of PCA result array for given outcome.
 *
 * @param {TypedArray} r array with PCA results.
 * @param {string} name name of the outcome (e.g. 'scores', 'hdist', 'qdist', 'expvar').
 * @param {number} a component number (for scores and distances).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function pcares_get(r, name, a) {

   if (!r || !r.class || !r.class.includes('pcares')) {
      throw new Error('pcares_get: parameter "r" must be JSON with PCA results.');
   }

   const ncols = r.ncols;
   const nrows = r.nrows;
   const ncomp = r.ncomp;
   let offset, length;

   switch(name) {
      case 'scores':
      case 'xscores':
         offset = (a - 1) * nrows;
         length = nrows;
         break;
      case 'T':
         offset = 0;
         length = nrows * ncomp;
         break;
      case 'hdist':
         offset = nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'qdist':
         offset = 2 * nrows * ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'xcumexpvar':
      case 'cumexpvar':
         offset = 3 * nrows * ncomp;
         length = ncomp;
         break;
      case 'fdistc':
         offset = 3 * nrows * ncomp + ncomp + (a - 1) * nrows;
         length = nrows;
         break;
      case 'fdistr':
         offset = 4 * nrows * ncomp + ncomp + (a - 1) * nrows;
         length = nrows;
         break;

      case 'gdist':
         offset = 5 * nrows * ncomp + ncomp + (a - 1) * ncols;
         length = ncols;
         break;
      case 'rdist':
         offset = (5 * nrows * ncomp) + ncomp + (ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;
      case 'idistc':
         offset = (5 * nrows * ncomp) + ncomp + (2 * ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;
      case 'idistr':
         offset = (5 * nrows * ncomp) + ncomp + (3 * ncols * ncomp) + (a - 1) * ncols;
         length = ncols;
         break;
      case 'E':
         offset = (5 * nrows * ncomp) + ncomp + (3 * ncols * ncomp) + ncomp * ncols;
         length = nrows * ncols;
         break;

      default:
         throw new Error('pcares_get: wrong outcome name.');
   }

   return r.v.subarray(offset, offset + length);
}


/**
 * Creates a PCA model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Matrix} X matrix with calibration set.
 * @param {number} ncomp number of components.
 * @param {boolean} center mean center the data columns or not.
 * @param {boolean} scale standardize the data columns or not.
 * @param {Array} outliers array with indices of outliers.
 * @param {Array} exclvars array with indices of excluded variables.
 *
 * @returns {Object} JSON with PCA model.
 */
export function pca_fit(Module, X, ncomp, center, scale, outliers, exclvars) {

   // check the dataset
   if (!ismatrix(X)) {
      throw new Error('pca_fit: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pca_fit: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pca_fit: indices in "outliers" array must be whole numbers.');

   // process excluded variables
   if (!exclvars) exclvars = [];
   const nexv = exclvars && Array.isArray(exclvars) ? exclvars.length : 0;
   if (!isArrCorrect(exclvars, 0, X.ncols - 1)) {
      throw new Error('pca_fit: some indices in "exclvars" array are outside the expected range.');
   }

   // the same truncation as for the outlier indices above: a variable index that is not a whole
   // number reaches C through 'Int32Array' as a different column, so the model would be fitted
   // without a variable the caller never named. a separate name for the same reason as above -
   // the model returned below carries the caller's own 'exclvars' array
   const exclvarValues = checkedIndexCopy(exclvars,
      'pca_fit: indices in "exclvars" array must be whole numbers.');

   const nrows = X.nrows - nout;
   const ncols = X.ncols - nexv;

   if (nrows < 3 || ncols < 2) {
      throw new Error('pca_fit: X must have at least 3 rows and 2 columns.');
   }


   // set the default values
   if (center === undefined) {
      center = true;
   }

   if (scale === undefined) {
      scale = false;
   }

   if (ncomp === undefined) {
      ncomp = Math.min(nrows - 1, ncols);
   }

   if (ncomp < 1 || ncomp > Math.min(nrows - 1, ncols)) {
      throw new Error('pca_fit: the value of "ncomp" does not match the dimension of X.');
   }


   // pointers and the result copy are hoisted so the 'finally' below can free every allocation no
   // matter where a throw happens, and so the copy outlives the block. a pointer which was never
   // assigned stays 0, and '_free(0)' is a no-op, so the frees below need no guards
   const m_length = pca_getmodellength(ncols, ncomp);
   const m_constructor = Float64Array;
   let x_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0;
   let model;

   try {

      // allocate memory in WASM heap space and copy dataset values
      x_ptr = allocCopy(Module, 'HEAPF64', X.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(exclvarValues), 'excluded variables');
      }

      // allocate memory in WASM heap space for model and calibration set results outcomes
      m_ptr = alloc(Module, m_length * m_constructor.BYTES_PER_ELEMENT, 'model');

      // call WASM function for PCA
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall('pcafit_wasm',
                  ['number'], // return type
                  ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
                  [x_ptr, X.nrows, X.ncols, ncomp, center, scale, m_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'pca_fit', mark);
      }

      // copy part of HEAP memory with model outcomes. this has to stay a 'slice' - it makes an
      // independent buffer, while a 'subarray' would be a view into memory the 'finally' frees
      model = Module.HEAPF64.slice(
         m_ptr/m_constructor.BYTES_PER_ELEMENT,
         m_ptr/m_constructor.BYTES_PER_ELEMENT + m_length
      );

   } finally {

      // freeing after a wasm trap is expected to be safe: the instance survives an ordinary trap
      // and every pointer above is either 0 or valid. a trap caused by earlier heap corruption
      // could still leave the allocator damaged, and a throw from here would then mask the original
      // error - accepted rather than guarded, since this build exports no abort flag
      Module._free(x_ptr);
      Module._free(m_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return PCA model and result objects
   return {
      class: ['pcamodel'],
      v: model,
      ncomp: ncomp,
      nrows: nrows,
      ncols: ncols,
      exclvars: exclvars,
      center: center,
      scale: scale
   };
}


/**
 * Projects dataset to a PCA model.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Object} JSON with PCA results.
 */
export function pca_predict(Module, m, X, outliers) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pca_predict: parameter "m" must be JSON with PCA model.');
   }

   // the model buffer is validated before anything is allocated: 'r_length' below is computed
   // from the metadata while C lays the result out from the header, so the two have to be known
   // to agree first
   checkModelBuffer(m, 'pca_predict', [[0, 'ncols'], [1, 'ncomp']], pca_getmodellength);

   if (!ismatrix(X)) {
      throw new Error('pca_predict: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pca_predict: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pca_predict: indices in "outliers" array must be whole numbers.');


   // compute numbers of rows and columns without excluded ones
   const nrows = X.nrows - nout;

   // the model keeps the caller's 'exclvars' array by REFERENCE, and a model read from a file
   // brings its own, so these values have never been through the check in the fit function - the
   // caller can even mutate the array after fitting. the truncation is the same: a column index
   // that is not a whole number reaches C as a different column.
   //
   // the container is tested here because this path has no 'isArrCorrect' to do it. everything
   // computed from these indices reads the dense copy. that is naming discipline, not a second
   // line of defence: the guard above has already refused every list the copy would differ from,
   // and nothing here is async, so re-reading 'm.exclvars' would be harmless. the result hands
   // 'm.exclvars' itself back as metadata, unchanged. see 'checkedIndexCopy'
   if (!Array.isArray(m.exclvars)) {
      throw new Error('pca_predict: indices in "exclvars" taken from the model must be whole numbers.');
   }
   const modelExclvars = checkedIndexCopy(m.exclvars,
      'pca_predict: indices in "exclvars" taken from the model must be whole numbers.');

   // excluded variables are taken from the model
   let exclvars = modelExclvars;

   // check if dataset alread excluded variables the ecluded variables array will be emptied
   if (X.ncols === m.ncols) {
      exclvars = [];
   }

   const nexv = exclvars.length;

   // compute new number of columns
   const ncols = X.ncols - nexv;

   if (nrows < 1) {
      throw new Error('pca_predict: X must have at least 1 row.');
   }

   if (ncols !== m.ncols) {
      throw new Error('pca_predict: X must have the same number of columns (without excluded ones) as the training set.');
   }

   // see the note in 'pca_fit' - pointers and the copy are hoisted so the 'finally' can free
   // unconditionally and the copy outlives the block
   const r_length = pca_getreslength(nrows, m.ncomp, ncols);
   const r_constructor = Float64Array;
   let x_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, r_ptr = 0;
   let res_values;

   try {

      // allocate memory in WASM heap space and copy dataset values
      // here we send the full dataset including outliers and excluded variables
      x_ptr = allocCopy(Module, 'HEAPF64', X.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(exclvars), 'excluded variables');
      }

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for result values
      r_ptr = alloc(Module, r_length * r_constructor.BYTES_PER_ELEMENT, 'results');

      // call WASM function for PCA prediction
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall('pcapredict_wasm',
                  ['number'], // return type
                  ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
                  [x_ptr, X.nrows, X.ncols, m_ptr, r_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'pca_predict', mark);
      }

      // copy part of HEAP memory with result values - 'slice', not 'subarray', see 'pca_fit'
      res_values = Module.HEAPF64.slice(
         r_ptr/r_constructor.BYTES_PER_ELEMENT,
         r_ptr/r_constructor.BYTES_PER_ELEMENT + r_length
      );

   } finally {
      Module._free(x_ptr);
      Module._free(m_ptr);
      Module._free(r_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // create object for PCA results
   const resobj = {
      class: ['pcares'],
      v: res_values,
      ncomp: m.ncomp,
      nrows: nrows,
      ncols: ncols,
      npred: ncols, // for compatibility
      outliers: outliers,
      exclvars: m.exclvars,
      center: m.center,
      scale: m.scale,
   };

   // compute object with statistics
   const expvar = new Array(m.ncomp);
   const resvar = new Array(m.ncomp);
   const cumresvar = new Array(m.ncomp);
   const cumexpvar = new Array(m.ncomp);
   const components = new Array(m.ncomp);

   const v = pcares_get(resobj, "xcumexpvar");
   components[0] = 1;
   cumexpvar[0] = v[0];
   cumresvar[0] = 1 - v[0];
   expvar[0] = v[0];
   resvar[0] = 1 - v[0];
   for (let i = 1; i < m.ncomp; i++) {
      components[i] = i + 1;
      cumexpvar[i] = v[i];
      cumresvar[i] = 1 - v[i];
      expvar[i] = v[i] - v[i - 1];
      resvar[i] = 1 - expvar[i];
   }

   // add statistics and return
   resobj.stat = {components, resvar, expvar, cumexpvar, cumresvar };
   return resobj;
}


/**
 * Create a PV-set based on PCA decomposition.
 *
 * @param {Object} Module object with wasm module for PCVPCA.
 * @param {Object} m JSON with PCA model created by `pca_fit()`.
 * @param {Matrix} X matrix with dataset.
 * @param {number} ncomp number of components for PV-set generation.
 * @param {number} cv_nseg number of segments for cross-validation.
 * @param {number} cv_type type of cross-validation (0: loo, 1: ven, 2: rand).
 * @param {number} cv_scope scope of cross-validation (0: global, 1: local).
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Matrix} the generated PV-set.
 */
export function pcvpca(Module, m, X, ncomp, cv_nseg, cv_type, cv_scope, outliers) {

   if (!m || !m.class || !m.class.includes('pcamodel')) {
      throw new Error('pcapcv: parameter "m" must be JSON with PCA results.');
   }

   // the other messages in this function say 'pcapcv', but the exported name is 'pcvpca' and the
   // C symbol is 'pcvpca_wasm' - a message whose whole job is to name the wrapper should name it
   checkModelBuffer(m, 'pcvpca', [[0, 'ncols'], [1, 'ncomp']], pca_getmodellength);

   if (!ismatrix(X)) {
      throw new Error('pcapcv: X must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, X.nrows - 1)) {
      throw new Error('pcvpca: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pcvpca: indices in "outliers" array must be whole numbers.');

   // the model keeps the caller's 'exclvars' array by REFERENCE, and a model read from a file
   // brings its own, so these values have never been through the check in the fit function - the
   // caller can even mutate the array after fitting. the truncation is the same: a column index
   // that is not a whole number reaches C as a different column.
   //
   // the container is tested here because this path has no 'isArrCorrect' to do it, and
   // everything computed from these indices reads the dense copy. that is naming discipline, not
   // a second line of defence: the guard above has already refused every list the copy would
   // differ from, and nothing here is async. see 'checkedIndexCopy'
   if (!Array.isArray(m.exclvars)) {
      throw new Error('pcvpca: indices in "exclvars" taken from the model must be whole numbers.');
   }
   const modelExclvars = checkedIndexCopy(m.exclvars,
      'pcvpca: indices in "exclvars" taken from the model must be whole numbers.');

   // excluded variables are taken from the model
   const nexv = modelExclvars.length;

   // compute numbers of rows and columns without excluded ones
   const nrows = X.nrows - nout;
   const ncols = X.ncols - nexv;


   if (nrows < 3) {
      throw new Error('pcapcv: X must have at least 3 rows.');
   }

   if (ncols !== m.ncols) {
      throw new Error('pcapcv: X must have the same number of columns as the training set.');
   }

   if (!Number.isInteger(cv_type) || ![0, 1, 2].includes(cv_type)) {
      throw new Error('pcapcv: parameter "cv_type" must be one of 0 ("loo"), 1 ("ven"), 2 ("rand).');
   }

   if (!Number.isInteger(cv_scope) || ![0, 1].includes(cv_scope)) {
      throw new Error('pcapcv: parameter "cv_scope" must be 0 ("global") or 1 ("local").');
   }

   if (cv_type > 0 && (!Number.isInteger(cv_nseg) || cv_nseg < 2)) {
      throw new Error('pcapcv: parameter "cv_nseg" must be a number ≥ 2.');
   }

   if (cv_type > 0 && cv_nseg > nrows) {
      throw new Error('pcapcv: the value of "cv_nseg" is too large for the given X.');
   }

   if (!Number.isInteger(ncomp) || ncomp < 1) {
      throw new Error('pcapcv: the value of "ncomp" must be a number >= 1.');
   }

   const max_ncomp = cv_type === 0 ?
      Math.min(nrows - 2, ncols, m.ncomp) :
      Math.min(nrows - Math.ceil(nrows / cv_nseg) - 1, ncols, m.ncomp);

   if (ncomp > max_ncomp) {
      throw new Error('pcapcv: the value of "ncomp" is too large.');
   }


   // see the note in 'pca_fit' - pointers and the copy are hoisted so the 'finally' can free
   // unconditionally and the copy outlives the block
   const xpv_length = nrows * ncols;
   const xpv_constructor = X.v.constructor;
   let x_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, xpv_ptr = 0;
   let xpv;

   try {

      // allocate memory in WASM heap space and copy dataset values
      x_ptr = allocCopy(Module, 'HEAPF64', X.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(modelExclvars), 'excluded variables');
      }

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for Xpv values
      xpv_ptr = alloc(Module, xpv_length * xpv_constructor.BYTES_PER_ELEMENT, 'Xpv');

      // call WASM function for PCAPCV
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall('pcvpca_wasm',
                  ['number'], // return type
                  ['number', 'number', 'number', 'number', 'number', 'number',
                  'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
                  [x_ptr, X.nrows, X.ncols, ncomp, m.center, m.scale,
                  cv_type, cv_nseg, cv_scope, m_ptr, xpv_ptr, o_ptr, nout, ev_ptr, nexv]); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'pcapcv', mark);
      }

      // copy part of HEAP memory with Xpv values - 'slice', not 'subarray', see 'pca_fit'
      xpv = Module.HEAPF64.slice(
         xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT,
         xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT + xpv_length
      );

   } finally {
      Module._free(x_ptr);
      Module._free(m_ptr);
      Module._free(xpv_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return Xpv
   return new Matrix(xpv, nrows, ncols);
}
